import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { ButtonComponent, Color, Size, TooltipDirective, Variant } from '@mode/capra';
import { SharedUtilSegmentModule, TrackClickData } from '@mode/shared/util-segment';
import { ClipboardDirective } from '../clipboard/clipboard.directive';

type ClipboardCompatTag = 'a' | 'button' | 'capra-button' | 'div' | 'li';

/**
 @description This component should only be used whenever "copy to clipboard" functionality
 * is needed in the legacy code (either AngularJS templates or .erb files) because it's not
 * possible to use modern Angular directives inside legacy contexts. If implementing said
 * functionality inside a modern Angular component, attach the `ClipboardDirective directly
 * to the desired element instead.

 @example (legacy)
 <mode-clipboard-compat
   tag="capra-button"
   text="Copy"
   clipboard-text="The value to be copied"
 ></mode-clipboard-compat>

 @example (modern)
 <capra-button
   text="Copy"
   modeClipboard
   clipboardText="The value to be copied"
 ></capra-button>
*/
@Component({
  standalone: true,
  selector: 'mode-clipboard-compat',
  imports: [ButtonComponent, ClipboardDirective, CommonModule, TooltipDirective, SharedUtilSegmentModule],
  templateUrl: './clipboard-compat.component.html',
  styleUrls: ['./clipboard-compat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClipboardCompatComponent {
  @Input() tag: ClipboardCompatTag = 'button';
  @Input() tagClasses = '';
  @Input() disabled = false;
  @Input() tooltipContent = '';
  @Input() tooltipContentIsHtml = false;
  @Input() clipboardText = '';
  @Input() inferHostClassDisplay = true;

  // Pass-through values specific to the 'capra-button' option
  @Input() text = '';
  @Input() variant: Variant = 'secondary';
  @Input() size: Size = 'small';
  @Input() color: Color = 'neutral';
  // ===

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: TS2564
  @Input() trackClickData: TrackClickData;
  @Output() clipboardSuccess = new EventEmitter<void>();
  @Output() clipboardError = new EventEmitter<void>();
  public tooltipDelay = [750, 0];
  private _stylesDict: Record<ClipboardCompatTag, 'flex' | 'inline-flex'> = {
    a: 'flex',
    button: 'inline-flex',
    'capra-button': 'inline-flex',
    div: 'flex',
    li: 'flex',
  };

  public get tooltipDisabled(): boolean {
    return !this.tooltipContent.length;
  }

  @HostBinding('class')
  public get hostClass() {
    return this.inferHostClassDisplay ? this._stylesDict[this.tag] : '';
  }
}
